// MyEmailView — Connect / disconnect Gmail or Microsoft 365 mailbox so the
// agent's mass email, doc-request notifications and signature requests go out
// from their REAL inbox instead of DoNotReply@azurecomm.net.
//
// Backend: GET /api/v1/email/status   POST /google/connect | /microsoft/connect
//          DELETE /api/v1/email
// Callback: backend redirects to /oauth-callback?status=success&provider=...
//           which posts a message to this window via the OAuthCallbackPage.

import React, { useEffect, useState, useCallback } from "react";
import { Mail, Check, AlertCircle, Loader2, Unplug, RefreshCw, Shield } from "lucide-react";
import {
  getEmailIntegrationStatus,
  connectGoogleEmail,
  connectMicrosoftEmail,
  disconnectEmail,
} from "../api.js";
import { confirmDialog } from "./ui.jsx";
import { textoDeErrorDelCalendario } from "../errorDelCalendario.js";

export function MyEmailView({ currentUser, lang = "en" }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // 'google' | 'microsoft' | 'disconnect' | null
  const [error, setError] = useState("");
  const [flash, setFlash] = useState("");

  const t = (es, en) => (lang === "es" ? es : en);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const s = await getEmailIntegrationStatus();
      setStatus(s);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Listen for the popup OAuth-callback page posting back its result
  useEffect(() => {
    function onMsg(ev) {
      // Audit F-N9: only trust postMessage from our own origin. The OAuth
      // popup we open lives at `${window.location.origin}/auth/...`, so any
      // cross-origin sender is either a misconfigured iframe or a phishing
      // attempt and we drop the message silently.
      if (ev.origin !== window.location.origin) return;
      if (!ev?.data || ev.data.type !== "deskte-oauth-result") return;
      // Only mailbox results. The same popup channel now carries calendar
      // (kind:'calendar') and telephony (kind:'phone') results, and without
      // this line a GoTo authorization would make this screen announce
      // "Connected to Microsoft 365". Absent kind = the legacy mailbox flow.
      if (ev.data.kind && ev.data.kind !== "email") return;
      if (ev.data.status === "success") {
        setFlash(t(
          `✅ Conectado a ${ev.data.provider === "google" ? "Gmail" : "Microsoft 365"} (${ev.data.email || ""})`,
          `✅ Connected to ${ev.data.provider === "google" ? "Gmail" : "Microsoft 365"} (${ev.data.email || ""})`,
        ));
        refresh();
      } else {
        setError(ev.data.error || "OAuth failed");
      }
      setBusy(null);
    }
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [refresh, lang]);

  async function startConnect(provider) {
    setError("");
    setFlash("");
    setBusy(provider);
    try {
      let authUrl;
      if (provider === "google") {
        ({ authUrl } = await connectGoogleEmail());
      } else if (provider === "microsoft365") {
        // Corporate M365 work account → /organizations/ endpoint.
        ({ authUrl } = await connectMicrosoftEmail("organizations"));
      } else {
        // Personal Outlook/Hotmail/Live → /consumers/ endpoint (default).
        ({ authUrl } = await connectMicrosoftEmail("consumers"));
      }
      const popup = window.open(
        authUrl,
        "deskte-oauth",
        "width=560,height=720,menubar=no,toolbar=no",
      );
      if (!popup) {
        // popup blocked — fall back to full redirect
        window.location.href = authUrl;
      }
    } catch (err) {
      setError(err.message || String(err));
      setBusy(null);
    }
  }

  async function handleDisconnect() {
    if (!(await confirmDialog({ message: t(
      "¿Desconectar tu correo? Las próximas campañas saldrán del remitente genérico de Deskte.",
      "Disconnect your email? Future campaigns will go from Deskte's generic sender.",
    ), danger: true }))) return;
    setBusy("disconnect");
    setError("");
    try {
      await disconnectEmail();
      setFlash(t("Cuenta desconectada", "Account disconnected"));
      await refresh();
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(null);
    }
  }

  const integ = status?.integration;
  const connected = !!integ;

  return (
    <div style={{ padding: "1.5rem", maxWidth: 880, margin: "0 auto" }}>
      <Header lang={lang} />

      {flash && <Banner kind="success" text={flash} onClose={() => setFlash("")} />}
      {error && <Banner kind="error"   text={error} onClose={() => setError("")} />}

      {loading ? (
        <Card>
          <div style={{ display: "flex", alignItems: "center", gap: 10, color: "var(--c-text2,#7aa3c0)" }}>
            <Loader2 size={16} className="dkt-spin" /> {t("Cargando estado…", "Loading status…")}
          </div>
        </Card>
      ) : connected ? (
        <ConnectedCard integ={integ} onDisconnect={handleDisconnect} onRefresh={refresh} busy={busy} lang={lang} />
      ) : (
        <ConnectChoices
          status={status}
          onConnect={startConnect}
          busy={busy}
          lang={lang}
        />
      )}

      <WhyConnect lang={lang} />
    </div>
  );
}

function Header({ lang }) {
  const t = (es, en) => (lang === "es" ? es : en);
  return (
    <div style={{ marginBottom: "1.5rem" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 12,
          background: "linear-gradient(135deg, #00d4ff22, #00d4ff08)",
          border: "1px solid #00d4ff44",
          display: "grid", placeItems: "center",
        }}>
          <Mail size={20} color="#00d4ff" />
        </div>
        <div>
          <h2 style={{ margin: 0, fontSize: "1.4rem", color: "var(--c-white,#eef6ff)" }}>
            {t("Mi Email", "My Email")}
          </h2>
          <div style={{ fontSize: ".82rem", color: "var(--c-text2,#7aa3c0)" }}>
            {t("Conecta tu Gmail o Microsoft 365 para que tus correos salgan de tu propia cuenta",
               "Connect your Gmail or Microsoft 365 so emails go out from your own inbox")}
          </div>
        </div>
      </div>
    </div>
  );
}

function Card({ children, accent }) {
  return (
    <div style={{
      background: "var(--c-surface,#0d1c2e)",
      border: `1px solid ${accent || "var(--c-border,#1a3550)"}`,
      borderRadius: 14,
      padding: "1.25rem",
      marginBottom: "1rem",
      boxShadow: "0 4px 16px rgba(0,0,0,.15)",
    }}>{children}</div>
  );
}

function Banner({ kind, text, onClose }) {
  const colors = kind === "success"
    ? { bg: "#0a3a2a", border: "#10b981", text: "#6ee7b7" }
    : { bg: "#3a1a1a", border: "#ef4444", text: "#fca5a5" };
  return (
    <div style={{
      background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 10,
      padding: ".75rem 1rem", marginBottom: ".75rem",
      display: "flex", alignItems: "center", gap: 10, color: colors.text, fontSize: ".88rem",
    }}>
      {kind === "success" ? <Check size={16} /> : <AlertCircle size={16} />}
      <span style={{ flex: 1 }}>{text}</span>
      {onClose && (
        <button onClick={onClose} style={{
          background: "none", border: "none", color: colors.text, cursor: "pointer", fontSize: 18, lineHeight: 1,
        }}>×</button>
      )}
    </div>
  );
}

function ConnectChoices({ status, onConnect, busy, lang }) {
  const t = (es, en) => (lang === "es" ? es : en);
  const googleEnabled = status?.googleConfigured;
  const msEnabled = status?.microsoftConfigured;

  return (
    <Card>
      <div style={{ marginBottom: "1rem" }}>
        <div style={{ fontSize: ".95rem", fontWeight: 700, color: "var(--c-white,#eef6ff)", marginBottom: 4 }}>
          {t("Conecta tu cuenta de correo", "Connect your email account")}
        </div>
        <div style={{ fontSize: ".82rem", color: "var(--c-text2,#7aa3c0)" }}>
          {t("Una sola vez. Puedes desconectarla cuando quieras.",
             "One-time setup. You can disconnect anytime.")}
        </div>
      </div>

      <div style={{ display: "grid", gap: ".75rem" }}>
        <ProviderButton
          provider="google"
          label="Google (Gmail / Workspace)"
          subtitle={t("@gmail.com o tu dominio con Google Workspace",
                      "@gmail.com or your domain on Google Workspace")}
          enabled={googleEnabled}
          loading={busy === "google"}
          onClick={() => onConnect("google")}
          lang={lang}
        />
        <ProviderButton
          provider="microsoft"
          label="Outlook personal (@outlook.com, @hotmail.com, @live.com)"
          subtitle={t("Cuenta personal Outlook/Hotmail/Live",
                      "Personal Outlook/Hotmail/Live account")}
          enabled={msEnabled}
          loading={busy === "microsoft"}
          onClick={() => onConnect("microsoft")}
          lang={lang}
        />
        <ProviderButton
          provider="microsoft365"
          label="Microsoft 365 corporativo (cuenta de trabajo)"
          subtitle={t("@tudominio.com con Microsoft 365 / Office 365 (cuenta laboral)",
                      "@yourdomain.com on Microsoft 365 / Office 365 (work account)")}
          enabled={msEnabled}
          loading={busy === "microsoft365"}
          onClick={() => onConnect("microsoft365")}
          lang={lang}
        />
      </div>

      {(!googleEnabled || !msEnabled) && (
        <div style={{ marginTop: ".75rem", fontSize: ".75rem", color: "var(--c-text3,#45688a)", lineHeight: 1.5 }}>
          {!googleEnabled && t(
            "⚠️ Google OAuth no configurado en este servidor. ",
            "⚠️ Google OAuth not configured on this server. ",
          )}
          {!msEnabled && t(
            "⚠️ Microsoft OAuth no configurado en este servidor. ",
            "⚠️ Microsoft OAuth not configured on this server. ",
          )}
        </div>
      )}
    </Card>
  );
}

function ProviderButton({ provider, label, subtitle, enabled, loading, onClick, lang }) {
  const t = (es, en) => (lang === "es" ? es : en);
  const colors = provider === "google"
    ? { bg: "#fff", text: "#3c4043", border: "#dadce0", brand: "#4285f4" }
    : { bg: "#2f2f2f", text: "#fff", border: "#444", brand: "#0078d4" };
  return (
    <button
      onClick={onClick}
      disabled={!enabled || loading}
      style={{
        display: "flex", alignItems: "center", gap: 14,
        padding: "1rem 1.25rem", borderRadius: 12,
        background: colors.bg, color: colors.text,
        border: `1px solid ${colors.border}`,
        cursor: enabled && !loading ? "pointer" : "not-allowed",
        opacity: enabled ? 1 : 0.5,
        textAlign: "left",
        transition: "transform .1s, box-shadow .15s",
        fontSize: ".9rem",
      }}
      onMouseDown={(e) => { if (enabled) e.currentTarget.style.transform = "scale(.98)"; }}
      onMouseUp={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
    >
      {provider === "google" ? <GoogleLogo /> : <MicrosoftLogo />}
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700 }}>{label}</div>
        <div style={{ fontSize: ".75rem", opacity: .75, marginTop: 2 }}>{subtitle}</div>
      </div>
      {loading ? (
        <Loader2 size={16} className="dkt-spin" />
      ) : (
        <span style={{ fontSize: ".82rem", fontWeight: 700, color: colors.brand }}>
          {t("Conectar →", "Connect →")}
        </span>
      )}
    </button>
  );
}

function ConnectedCard({ integ, onDisconnect, onRefresh, busy, lang }) {
  const t = (es, en) => (lang === "es" ? es : en);
  const providerLabel = integ.provider === "GOOGLE" ? "Gmail" : "Microsoft 365";
  return (
    <Card accent="#10b98155">
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: "1rem" }}>
        <div style={{
          width: 42, height: 42, borderRadius: 12,
          background: "#10b98122", border: "1px solid #10b98166",
          display: "grid", placeItems: "center",
        }}>
          <Check size={20} color="#10b981" />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: ".75rem", color: "#10b981", fontWeight: 700, letterSpacing: ".05em" }}>
            {t("CONECTADO", "CONNECTED")}
          </div>
          <div style={{ fontSize: "1.05rem", fontWeight: 700, color: "var(--c-white,#eef6ff)", marginTop: 2 }}>
            {integ.email}
          </div>
          <div style={{ fontSize: ".75rem", color: "var(--c-text2,#7aa3c0)", marginTop: 2 }}>
            {providerLabel} · {integ.displayName || ""}
          </div>
        </div>
      </div>

      <div style={{
        background: "var(--c-surface2,#112538)", borderRadius: 10, padding: ".75rem 1rem", marginBottom: ".75rem",
        fontSize: ".82rem", color: "var(--c-text2,#7aa3c0)", lineHeight: 1.6,
      }}>
        {t(
          "Tus correos masivos, solicitudes de documentos y firmas saldrán desde ",
          "Your mass emails, document requests and signatures will go out from ",
        )}
        <strong style={{ color: "var(--c-cyan,#00d4ff)" }}>{integ.email}</strong>{". "}
        {t(
          "Los clientes verán tu nombre real y podrán responder directo a tu bandeja de entrada.",
          "Clients see your real name and can reply straight to your inbox.",
        )}
      </div>

      <div style={{ display: "flex", gap: ".5rem" }}>
        <button onClick={onRefresh} disabled={busy} style={{
          flex: 1, padding: ".7rem", borderRadius: 10,
          background: "var(--c-surface2,#112538)", color: "var(--c-text2,#7aa3c0)",
          border: "1px solid var(--c-border,#1a3550)", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
          fontSize: ".82rem", fontWeight: 600,
        }}>
          <RefreshCw size={13} /> {t("Refrescar", "Refresh")}
        </button>
        <button onClick={onDisconnect} disabled={busy === "disconnect"} style={{
          flex: 1, padding: ".7rem", borderRadius: 10,
          background: "#7f1d1d", color: "#fecaca",
          border: "1px solid #ef444466", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
          fontSize: ".82rem", fontWeight: 600,
        }}>
          {busy === "disconnect" ? <Loader2 size={13} className="dkt-spin" /> : <Unplug size={13} />}
          {t("Desconectar", "Disconnect")}
        </button>
      </div>
    </Card>
  );
}

function WhyConnect({ lang }) {
  const t = (es, en) => (lang === "es" ? es : en);
  const items = [
    {
      icon: Shield,
      title: t("No más SPAM", "No more SPAM"),
      body: t(
        "Cuando un correo sale desde tu propio Gmail/Outlook, llega a la bandeja principal — no a la carpeta de spam.",
        "When email comes from your own Gmail/Outlook, it lands in the inbox — not the spam folder.",
      ),
    },
    {
      icon: Mail,
      title: t("Tu identidad real", "Your real identity"),
      body: t(
        "Los clientes ven tu nombre y pueden responderte directo. No DoNotReply@deskte.",
        "Clients see your name and can reply directly. No more DoNotReply@deskte.",
      ),
    },
    {
      icon: RefreshCw,
      title: t("Sin límites del remitente compartido", "No shared-sender rate limits"),
      body: t(
        "Manda todas las firmas y campañas que necesites — los límites son los de tu propio Gmail/Outlook.",
        "Send all the signatures and campaigns you need — limits come from your own Gmail/Outlook.",
      ),
    },
  ];
  return (
    <div style={{ marginTop: "1.5rem" }}>
      <div style={{ fontSize: ".82rem", color: "var(--c-text3,#45688a)", letterSpacing: ".05em", textTransform: "uppercase", marginBottom: ".75rem", fontWeight: 700 }}>
        {t("¿Por qué conectar?", "Why connect?")}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: ".75rem" }}>
        {items.map((it, i) => (
          <div key={i} style={{
            background: "var(--c-surface,#0d1c2e)", border: "1px solid var(--c-border,#1a3550)",
            borderRadius: 12, padding: "1rem",
          }}>
            <it.icon size={18} color="#00d4ff" />
            <div style={{ fontSize: ".88rem", fontWeight: 700, color: "var(--c-white,#eef6ff)", marginTop: 8, marginBottom: 4 }}>
              {it.title}
            </div>
            <div style={{ fontSize: ".78rem", color: "var(--c-text2,#7aa3c0)", lineHeight: 1.5 }}>
              {it.body}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function GoogleLogo() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
    </svg>
  );
}

function MicrosoftLogo() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path fill="#F25022" d="M1 1h10v10H1z"/>
      <path fill="#7FBA00" d="M13 1h10v10H13z"/>
      <path fill="#00A4EF" d="M1 13h10v10H1z"/>
      <path fill="#FFB900" d="M13 13h10v10H13z"/>
    </svg>
  );
}

// ----------------------------------------------------------------------
// OAuthCallbackPage — rendered when the OAuth provider redirects back to
// /oauth-callback?status=...&provider=...&email=...&error=...
// Posts result to the opener (the Mi Email tab) and self-closes.
// If opener is missing (popup blocked, full redirect was used), shows a
// "you can close this window now" screen with a button back to the app.
// ----------------------------------------------------------------------

export function OAuthCallbackPage() {
  const params = new URLSearchParams(window.location.search);
  const status = params.get("status") || "error";
  const provider = params.get("provider") || "";
  const email = params.get("email") || "";
  const error = params.get("error") || "";
  // 'calendar' for the two-way calendar-sync flow, 'phone' for a telephony
  // provider (GoTo), '' / 'email' for mailbox. Forwarded so each screen can
  // tell its own result apart from the others'.
  const kind = params.get("kind") || "email";
  // GoTo only: whether we found the line its calls go out from. Forwarded
  // because the phone screen has to nag for it right away — authorized with no
  // line still cannot place a call.
  const line = params.get("line") || "";
  const ok = status === "success";

  useEffect(() => {
    try {
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage(
          { type: "deskte-oauth-result", status, provider, email, error, kind, line },
          window.location.origin,
        );
        // Give the parent a tick to receive the message before closing.
        setTimeout(() => window.close(), 300);
      }
    } catch {
      // If postMessage failed, the manual screen below is the fallback.
    }
  }, [status, provider, email, error, kind, line]);

  return (
    <div style={{
      minHeight: "100vh", background: "#070d14",
      display: "grid", placeItems: "center", padding: "2rem",
      color: "#eef6ff", fontFamily: "system-ui,sans-serif",
    }}>
      <div style={{
        maxWidth: 460, width: "100%",
        background: "#0d1c2e", border: "1px solid #1a3550", borderRadius: 16,
        padding: "2rem", textAlign: "center", boxShadow: "0 24px 60px rgba(0,0,0,.4)",
      }}>
        <div style={{
          width: 64, height: 64, borderRadius: 16, margin: "0 auto 1rem",
          background: ok ? "#10b98122" : "#ef444422",
          border: `1px solid ${ok ? "#10b981" : "#ef4444"}`,
          display: "grid", placeItems: "center",
        }}>
          {ok ? <Check size={28} color="#10b981" /> : <AlertCircle size={28} color="#ef4444" />}
        </div>
        <h2 style={{ margin: 0, fontSize: "1.35rem" }}>
          {ok ? "✅ Conectado" : "❌ Error"}
        </h2>
        {ok ? (
          <>
            <div style={{ marginTop: 8, color: "#7aa3c0" }}>
              {provider === "google" ? "Gmail" : provider === "goto" ? "GoTo Connect" : "Microsoft 365"}
              {/* GoTo connects a phone account, not a mailbox: there is no
                  address to show, and the old dash left a dangling "— ". */}
              {email ? <> — <strong style={{ color: "#00d4ff" }}>{email}</strong></> : null}
            </div>
            <div style={{ marginTop: 16, fontSize: ".85rem", color: "#7aa3c0" }}>
              Puedes cerrar esta ventana / You can close this window
            </div>
          </>
        ) : (
          <div style={{ marginTop: 12, color: "#fca5a5", fontSize: ".88rem" }}>
            {(kind === "calendar" ? textoDeErrorDelCalendario(error, "es") : error) || "Unknown error"}
          </div>
        )}
        <button
          onClick={() => { try { window.close(); } catch {} window.location.href = "/"; }}
          style={{
            marginTop: 20, padding: ".7rem 1.5rem", borderRadius: 10,
            background: "#00d4ff", color: "#0a1628", border: "none", cursor: "pointer",
            fontWeight: 700, fontSize: ".9rem",
          }}
        >
          Cerrar / Close
        </button>
      </div>
    </div>
  );
}
